import { BkfcRequestError } from "./api";

// Ported from the Aptos reference app (apps/web/src/lib/errors.ts).
// Every message matched here was captured from a real rejection, not guessed.
// wallet-adapter-core's signAndSubmitTransaction unwraps any object with a
// message field down to that bare string before it reaches app code, so
// classification matches on message content, not typeof or instanceof.
export type BkfcErrorKind =
  | "spend-limit-exceeded"
  | "rate-limit-exceeded"
  | "not-allowlisted"
  | "invalid-api-key"
  | "wallet-rejected"
  | "on-chain-abort"
  | "unconfirmed-timeout"
  | "wallet-not-installed"
  | "already-claimed"
  | "signer-unavailable"
  | "unknown";

export interface BkfcClassifiedError {
  kind: BkfcErrorKind;
  title: string;
  description: string;
  /** The specific real-world detail (server message, vm_status, ...), when available. */
  detail?: string;
  raw: unknown;
}

const COPY: Record<BkfcErrorKind, { title: string; description: string }> = {
  "spend-limit-exceeded": {
    title: "Sponsorship limit reached",
    description:
      "Free claiming has hit its cap for the current window. Please try again a bit later.",
  },
  "rate-limit-exceeded": {
    title: "Too many requests",
    description:
      "Too many claims in a short window. Wait a moment and try again.",
  },
  "not-allowlisted": {
    title: "Claiming temporarily unavailable",
    description:
      "The claim function isn't enabled on the gas sponsor yet. Please contact support.",
  },
  "invalid-api-key": {
    title: "Claiming temporarily unavailable",
    description:
      "The gas sponsorship configuration was rejected. Please contact support.",
  },
  "wallet-rejected": {
    title: "Request declined",
    description: "You declined the request in your wallet.",
  },
  "on-chain-abort": {
    title: "Transaction rejected",
    description:
      "The network rejected this transaction. This collectible may already be claimed or the authorization expired — please try again.",
  },
  "unconfirmed-timeout": {
    title: "Still pending",
    description:
      "The transaction was submitted but hasn't confirmed yet. It may still complete — please try again in a moment.",
  },
  "wallet-not-installed": {
    title: "Wallet not installed",
    description: "This wallet isn't installed. Install it, then reconnect.",
  },
  "already-claimed": {
    title: "Already claimed",
    description: "This collectible has already been claimed or is invalid.",
  },
  "signer-unavailable": {
    title: "Service unavailable",
    description:
      "Couldn't reach the service that authorizes claims. Check your connection and try again shortly.",
  },
  unknown: {
    title: "Something went wrong",
    description: "An unexpected error occurred. Please try again.",
  },
};

function classified(
  kind: BkfcErrorKind,
  detail: string | undefined,
  raw: unknown,
): BkfcClassifiedError {
  return { kind, ...COPY[kind], detail, raw };
}

// handles all three shapes a message shows up in: bare string, Error.message,
// object.message. Same rejection, same classification, regardless of layer.
function classifyMessage(message: string, raw: unknown): BkfcClassifiedError {
  if (message.startsWith("Gas limit exceeded"))
    return classified("spend-limit-exceeded", message, raw);
  if (message.startsWith("Rule not found"))
    return classified("not-allowlisted", message, raw);
  if (message.startsWith("Simulation failed"))
    return classified("on-chain-abort", message, raw);
  if (message.includes("rate limit exceeded"))
    return classified("rate-limit-exceeded", message, raw);
  if (message.startsWith("Unauthorized"))
    return classified("invalid-api-key", message, raw);
  if (message.includes("User has rejected the request"))
    return classified("wallet-rejected", undefined, raw);
  return classified("unknown", message, raw);
}

// covers the backend signature call, before any wallet interaction. different
// failure modes than the gas station (already claimed, unreachable).
export function classifySignerError(err: unknown): BkfcClassifiedError {
  if (err instanceof BkfcRequestError) {
    // match on message too: the DB-level guard shipped as a 400 before it was
    // aligned to 409, so a not-yet-redeployed backend still classifies right
    if (err.status === 409 || /already been claimed/i.test(err.message))
      return classified("already-claimed", err.message, err);
    if (err.status === 0) return classified("signer-unavailable", err.message, err);
    return classified("unknown", err.message, err);
  }
  return classified(
    "unknown",
    err instanceof Error ? err.message : undefined,
    err,
  );
}

export function classifyGasStationError(err: unknown): BkfcClassifiedError {
  if (typeof err === "string") return classifyMessage(err, err);

  if (err instanceof Error) {
    // only the SDK's own waitForTransaction errors have these, wallet adapter errors don't
    if ("lastSubmittedTransaction" in err)
      return classified("unconfirmed-timeout", err.message, err);
    if ("transaction" in err)
      return classified("on-chain-abort", err.message, err);
    return classifyMessage(err.message, err);
  }

  if (err && typeof err === "object" && "message" in err) {
    return classifyMessage(String((err as { message: unknown }).message), err);
  }

  return classified("unknown", undefined, err);
}
